import React from 'react';
import {
  ResponsiveContainer, ComposedChart, LineChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, ReferenceArea,
} from 'recharts';
import { INK, numFormat, pct, monthLabel, shortDate, axisProps, ChartTooltip, Delta, MetricRow, Section, TableToggle } from '../analyticsUi';
import {
  COLORS, TABLE_AGES, MIN_TABLE_USERS, LABELLED_AGES, eur, eurFormat, eurRange, ageLabel, ageToX, xToAgeLabel, horizonLabel,
} from './profitFormat';

const projectedCell = (cell, format, extraClass = '', key) => {
  if (!cell) return <td key={key} className={`pf-proj ${extraClass}`}>–</td>;
  const split = cell.ad != null ? `Subscriptions ${eur(cell.value - cell.ad, true)} · Ads ${eur(cell.ad, true)}` : undefined;
  return <td key={key} className={`${cell.projected ? 'pf-proj' : ''} ${extraClass}`} title={split}>{cell.projected ? '≈ ' : ''}{format(cell.value)}</td>;
};

export function LastThirtyDays({ last30, prev30, expectedNow, expectedPrev, expectedRange, accuracy, horizon }) {
  return (
    <Section title="Last 30 days">
      <div className="pf-hero">
        <div className="pf-tile-label">{expectedNow ? `Expected profit on this spend (${horizon})` : 'Cash profit'}</div>
        <div className="pf-hero-value">
          {eur(expectedNow ? expectedNow.profit : last30.profit)}
          <Delta value={expectedNow ? expectedNow.profit : last30.profit} previous={expectedNow ? expectedPrev?.profit : prev30.profit} short />
        </div>
        {expectedNow && (
          <div className="pf-figure-note">
            New users' expected earnings minus ad spend
            {expectedRange ? ` · likely ${eur(expectedRange.low)} to ${eur(expectedRange.high)}` : ''}
            {accuracy ? ` · forecasts off by ~${pct(accuracy.meanAbsError)}` : ''}
          </div>
        )}
      </div>
      <div className="pf-metrics">
        {expectedNow && <MetricRow label="Cash profit" note="revenue minus spend" value={last30.profit} previous={prev30.profit} format={eur} />}
        <MetricRow label="Revenue" note={`subs ${eur(last30.subRevenue)} · ads ${eur(last30.adRevenue)}`} value={last30.revenue} previous={prev30.revenue} format={eur} />
        <MetricRow label="Ad spend" value={last30.spend} previous={prev30.spend} goodWhenUp={null} format={eur} />
        <MetricRow label="New users" note={`${numFormat.format(last30.installs)} installs`} value={last30.signups} previous={prev30.signups} format={v => numFormat.format(v)} />
        {last30.trialsStarted > 0 && (
          <MetricRow label="Trials turning paid" note={`${last30.trialsConverted} of ${last30.trialsStarted}`} value={last30.trialsConverted / last30.trialsStarted} format={pct} />
        )}
      </div>
    </Section>
  );
}

export function MoneyPerMonth({ monthly, forecast, open, onToggle }) {
  return (
    <Section
      title="Money per month"
      actions={<TableToggle open={open} onToggle={onToggle} />}
    >
      <div className="pf-chart">
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={monthly} barGap={2} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke={INK.grid} />
            <XAxis dataKey="month" tickFormatter={monthLabel} {...axisProps} />
            <YAxis tickFormatter={v => eurFormat.format(v)} width={64} {...axisProps} axisLine={false} />
            <Tooltip content={<ChartTooltip labelFormatter={monthLabel} valueFormatter={v => eur(v, true)} />} cursor={{ fill: 'rgba(11,11,11,0.04)' }} />
            <Legend itemSorter={null} iconType="circle" wrapperStyle={{ fontSize: 13, color: INK.secondary }} />
            <ReferenceLine y={0} stroke={INK.axis} />
            <Bar isAnimationActive={false} dataKey="subRevenue" name="Subscriptions" stackId="rev" fill={COLORS.subs} maxBarSize={24} stroke="#fcfcfb" strokeWidth={1} />
            <Bar isAnimationActive={false} dataKey="adRevenue" name="Ad revenue" stackId="rev" fill={COLORS.ads} maxBarSize={24} radius={[4, 4, 0, 0]} stroke="#fcfcfb" strokeWidth={1} />
            <Bar isAnimationActive={false} dataKey="spend" name="Ad spend" fill={COLORS.spend} maxBarSize={24} radius={[4, 4, 0, 0]} />
            <Line isAnimationActive={false} dataKey="profit" name="Cash profit" stroke={COLORS.profit} strokeWidth={2} dot={{ r: 4, strokeWidth: 2, stroke: '#fcfcfb', fill: COLORS.profit }} />
            <Line isAnimationActive={false} dataKey="expected" name="Expected profit of the month's new users" stroke={COLORS.profit} strokeWidth={2} strokeDasharray="5 4" dot={{ r: 3, strokeWidth: 2, stroke: COLORS.profit, fill: '#fcfcfb' }} connectNulls />
            <Line isAnimationActive={false} dataKey="forecast" name="Forecast, ads as now" stroke={COLORS.profit} strokeWidth={2} strokeDasharray="1 4" strokeLinecap="round" dot={{ r: 3, strokeWidth: 2, stroke: COLORS.profit, fill: '#fcfcfb' }} connectNulls />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {open && (
        <div className="pf-table-wrap">
          <table className="pf-table">
            <thead><tr><th>Month</th><th>Installs</th><th>New users</th><th>Spend</th><th>Subscriptions</th><th>Ad revenue</th><th>Cash profit</th><th>Expected profit</th><th>Forecast</th></tr></thead>
            <tbody>
              {[...monthly].reverse().map(m => (
                <tr key={m.month}>
                  <td>{monthLabel(m.month)}</td><td>{m.installs == null ? '–' : numFormat.format(m.installs)}</td><td>{m.signups == null ? '–' : numFormat.format(m.signups)}</td>
                  <td>{m.spend == null ? '–' : eur(m.spend, true)}</td><td>{m.subRevenue == null ? '–' : eur(m.subRevenue, true)}</td><td>{m.adRevenue == null ? '–' : eur(m.adRevenue, true)}</td>
                  <td className={m.profit < 0 ? 'neg' : ''}>{m.profit == null ? '–' : eur(m.profit, true)}</td>
                  <td className={m.expected < 0 ? 'neg pf-proj' : 'pf-proj'}>{m.expected == null ? '–' : `≈ ${eur(m.expected, true)}`}</td>
                  <td className={m.forecast < 0 ? 'neg pf-proj' : 'pf-proj'}>{m.forecast == null ? '–' : `≈ ${eur(m.forecast, true)}`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {forecast && (
        <p className="pf-footnote">
          Forecast assumes {eur(forecast.spendPerDay, true)} of ad spend and {numFormat.format(Math.round(forecast.signupsPerDay))} new users a day, {forecast.paceFrom ? `the pace since ${shortDate(forecast.paceFrom)}` : 'the last 30 days’ pace'}.
        </p>
      )}
    </Section>
  );
}

export function EarningsPerUser({ typical, costLow, costHigh, renewalAdded, renewal }) {
  // Solid line for observed ages, dashed from the last observed point on
  const lastObservedAge = [...typical].reverse().find(p => !p.extrapolated)?.age;
  const typicalChart = typical.map(p => ({
    age: p.age,
    x: ageToX(p.age),
    value: p.extrapolated ? null : p.value,
    extrapolated: p.extrapolated || p.age === lastObservedAge ? p.value : null,
  }));

  return (
    <Section
      title="Earnings per user"
      hint="What a typical user has earned by each age. Above the shaded band, they've paid for themselves."
    >
      {typical.length === 0 ? (
        <p className="pf-empty">Not enough users with earnings yet.</p>
      ) : (
        <div className="pf-chart">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={typicalChart} margin={{ top: 20, right: 16, bottom: 0, left: 0 }}>
              <CartesianGrid vertical={false} stroke={INK.grid} />
              <XAxis
                dataKey="x"
                type="number"
                domain={[0, 'dataMax']}
                ticks={typicalChart.filter(p => LABELLED_AGES.includes(p.age)).map(p => p.x)}
                tickFormatter={xToAgeLabel}
                {...axisProps}
              />
              <YAxis tickFormatter={v => eur(v, true)} width={64} {...axisProps} axisLine={false} />
              <Tooltip
                content={(
                  <ChartTooltip
                    labelFormatter={x => `${xToAgeLabel(x)} after signup`}
                    valueFormatter={v => eur(v, true)}
                    filter={(p, all) => p.dataKey === 'value' || !all.some(q => q.dataKey === 'value' && q.value != null)}
                  />
                )}
              />
              {costLow > 0 && (costHigh
                ? (
                  <ReferenceArea
                    y1={costLow}
                    y2={costHigh}
                    ifOverflow="extendDomain"
                    fill={COLORS.spend}
                    fillOpacity={0.12}
                    stroke="none"
                    label={{ value: `Ad cost per new user ${eurRange(costLow, costHigh)}`, position: 'insideTopLeft', fill: INK.secondary, fontSize: 12 }}
                  />
                ) : (
                  <ReferenceLine
                    y={costLow}
                    ifOverflow="extendDomain"
                    stroke={COLORS.spend}
                    strokeDasharray="4 4"
                    label={{ value: `Ad cost per new user ${eur(costLow, true)}`, position: 'insideTopLeft', fill: INK.secondary, fontSize: 12 }}
                  />
                ))}
              <Line isAnimationActive={false} dataKey="value" name="Typical user" stroke={COLORS.avg} strokeWidth={2} dot={{ r: 4, strokeWidth: 2, stroke: '#fcfcfb', fill: COLORS.avg }} />
              <Line
                isAnimationActive={false}
                dataKey="extrapolated"
                name="Extrapolated"
                stroke={COLORS.avg}
                strokeWidth={2}
                strokeDasharray="5 4"
                dot={({ cx, cy, payload }) => (payload.age === lastObservedAge || cy == null
                  ? <g key={payload.age} />
                  : <circle key={payload.age} cx={cx} cy={cy} r={4} strokeWidth={2} stroke={COLORS.avg} fill="#fcfcfb" />)}
              />
              {typicalChart.some(p => p.extrapolated != null) && (
                <Legend iconType="plainline" wrapperStyle={{ fontSize: 13, color: INK.secondary }} />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      {renewalAdded && (
        <p className="pf-footnote">
          The dashed line includes yearly-plan renewals: the first about {renewal.age} days after signup ({eur(renewal.perUser, true)} per user), the second a year later at the same rate.{' '}
          {renewal.source === 'observed'
            ? `${pct(renewal.rate)} of yearly subscribers are expected to renew, based on renewals so far.`
            : `Cautious guess until the first renewals come in: ${pct(renewal.autoRenewOffShare)} of yearly subscribers have already turned off auto-renew, and only ${pct(renewal.keepIfOn)} of the rest are assumed to renew, so ${pct(renewal.rate)} in total.`}
        </p>
      )}
    </Section>
  );
}

export function SignupMonths({ cohortRows, returnAge, open, onToggle }) {
  return (
    <Section
      title="Signup months"
      hint="Italic ≈ numbers are projections."
      actions={<TableToggle open={open} onToggle={onToggle} />}
    >
      {open && (
        <>
          <div className="pf-table-wrap">
            <table className="pf-table">
              <thead>
                <tr className="pf-group-head">
                  <th colSpan={2} />
                  <th colSpan={2} className="pf-g-cost pf-col-start">Ad cost</th>
                  <th colSpan={TABLE_AGES.length} className="pf-g-earn pf-col-start">Earned per user, by time since signup</th>
                  <th colSpan={3} className="pf-g-result pf-col-start">Result{returnAge != null ? `, ${horizonLabel(returnAge)}` : ''}</th>
                </tr>
                <tr>
                  <th>Signup month</th><th>New users</th>
                  <th className="pf-g-cost pf-col-start">Total</th><th className="pf-g-cost">Per user</th>
                  {TABLE_AGES.map((a, i) => <th key={a} className={`pf-g-earn ${i === 0 ? 'pf-col-start' : ''}`}>{ageLabel(a)}</th>)}
                  <th className="pf-g-result pf-col-start">Pays back</th>
                  <th className="pf-g-result">Return per €1</th>
                  <th className="pf-g-result">Expected profit</th>
                </tr>
              </thead>
              <tbody>
                {cohortRows.filter(c => c.users >= MIN_TABLE_USERS).map(c => (
                  <tr key={c.month}>
                    <td>{monthLabel(c.month)}</td>
                    <td>{numFormat.format(c.users)}</td>
                    <td className="pf-col-start">{eur(c.spend)}</td>
                    <td>{c.cac ? eur(c.cac, true) : '–'}</td>
                    {TABLE_AGES.map((a, i) => projectedCell(c.projected[a], v => eur(v, true), `${i === 0 ? 'pf-col-start' : ''}`, a))}
                    <td className={`pf-col-start ${c.payback?.projected ? 'pf-proj' : ''}`}>
                      {c.payback ? `${c.payback.projected ? '≈ ' : ''}${ageLabel(c.payback.age)}` : c.cac > 0 ? 'Not yet' : '–'}
                    </td>
                    {projectedCell(c.return, v => eur(v, true))}
                    <td className={`pf-strong ${c.expectedProfit < 0 ? 'neg' : 'pos'}`}>{c.expectedProfit == null ? '–' : `≈ ${eur(c.expectedProfit)}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="pf-footnote">
            Earnings are subscriptions plus ad revenue (hover for the split); ≈ adds what older users went on to earn. Expected profit = new users × earnings at {returnAge != null ? horizonLabel(returnAge) : 'one year'} − ad spend.
          </p>
        </>
      )}
    </Section>
  );
}

export function Campaigns({ campaigns, since }) {
  if (!(campaigns?.length > 1)) return null;
  return (
    <Section
      title="Campaigns"
      hint={`${since ? `Since ${shortDate(since)}, when campaign data per user starts. ` : ''}Only what Google ties to a campaign, and these users are still young, so revenue is a lower bound. Good for comparing campaigns.`}
    >
      {(
        <div className="pf-table-wrap">
          <table className="pf-table pf-table-fit">
            <thead><tr><th>Campaign</th><th>Spend</th><th>Clicks</th><th>Installs credited</th><th>Users credited</th><th>Cost per user</th><th>Revenue so far</th><th>Return per €1</th></tr></thead>
            <tbody>
              {campaigns.map(c => (
                <tr key={c.name}>
                  <td>{c.name}</td><td>{eur(c.spend, true)}</td><td>{numFormat.format(c.clicks)}</td>
                  <td>{numFormat.format(c.installs)}</td><td>{numFormat.format(c.users)}</td>
                  <td>{c.costPerUser != null ? eur(c.costPerUser, true) : '–'}</td>
                  <td>{eur(c.revenue, true)}</td>
                  <td>{c.roas != null ? eur(c.roas, true) : '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}
